import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type {
    Blocks,
    FaqItem,
    Offer,
    SiteFrame
} from '@/types/content'

import Link from 'next/link'
import { ArrowUpRight, Check, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { CtaButton } from '@/components/generals/cta-button'
import { Layout } from '@/components/generals/layout'
import { SectionIntro } from '@/components/generals/section-intro'
import { Seo } from '@/components/generals/seo'
import { DraftBadge } from '@/components/sections/draft-badge'
import { FaqSection } from '@/components/sections/faq-section'
import { OfferImage } from '@/components/sections/offer-image'
import { OfferSpecs, SPEC_LABELS } from '@/components/sections/offer-specs'
import { cms, hasValue, isPlaceholder, padNumber } from '@/lib/cms'
import { reveal } from '@/lib/reveal'
import { resolveSiteUrl } from '@/lib/site-url'

import {
    REVALIDATE_SECONDS,
    getFaqs,
    getOfferBySlug,
    getOfferSlugs,
    getOffers,
    getPageContent
} from '@/lib/content'

interface OfferPageProps {
    frame: SiteFrame
    blocks: Blocks
    offer: Offer
    others: Array<Offer>
    faqs: Array<FaqItem>
}

interface DetailRowProps {
    label: string
    children: ReactNode
}

const DetailRow: FC<DetailRowProps> = ({ label, children }): ReactNode => {
    return (
        <div className='grid gap-3 border-t border-ink/15 py-8 md:grid-cols-12 md:gap-10' {...reveal()}>
            <h2 className='type-eyebrow text-ember md:col-span-4'>
                {label}
            </h2>

            <div className='md:col-span-8'>
                {children}
            </div>
        </div>
    )
}

const OfferPage: FC<OfferPageProps> = ({ frame, blocks, offer, others, faqs }): ReactNode => {
    const { settings } = frame
    const requestHref = `/kontakt?angebot=${offer.slug}`
    const siteUrl = resolveSiteUrl(settings.webseite)

    // Only confirmed offers are described to search engines as a service.
    const jsonLd = offer.bestaetigt
        ? [{
            '@context': 'https://schema.org',
            '@type': 'Service',
            name: offer.name,
            ...(hasValue(offer.kurzbeschreibung) ? { description: offer.kurzbeschreibung } : {}),
            provider: { '@type': 'Organization', name: settings.markenname, url: `${siteUrl}/` },
            url: `${siteUrl}/coaching/${offer.slug}`
        }]
        : []

    return (
        <Layout frame={frame}>
            <Seo
                title={offer.name}
                description={offer.kurzbeschreibung ?? undefined}
                siteUrl={settings.webseite}
                path={`/coaching/${offer.slug}`}
                ogImage={hasValue(offer.bild) ? offer.bild : undefined}
                jsonLd={jsonLd}
            />

            <section aria-labelledby='seite-titel' className='relative bg-bone pb-20 pt-10 sm:pb-28 sm:pt-14 lg:pb-32'>
                <Container>
                    <nav aria-label='Brotkrumen' className='type-label mb-12 text-smoke sm:mb-16'>
                        <ol className='flex flex-wrap items-center gap-2'>
                            <li>
                                <Link href='/' className='transition-colors duration-300 hover:text-ink'>Startseite</Link>
                            </li>
                            <li aria-hidden='true'><ChevronRight className='size-3' /></li>
                            <li>
                                <Link href='/coaching' className='transition-colors duration-300 hover:text-ink'>Coaching</Link>
                            </li>
                            <li aria-hidden='true'><ChevronRight className='size-3' /></li>
                            <li aria-current='page' className='text-ink'>
                                <span data-mdlfy-cms={cms('angebote', offer.id, 'name')}>{offer.name}</span>
                            </li>
                        </ol>
                    </nav>

                    <div className='grid gap-12 lg:grid-cols-12 lg:items-end lg:gap-10'>
                        <div className='flex flex-col gap-8 lg:col-span-7'>
                            <div className='flex flex-wrap items-center gap-4 animate-in fade-in fill-mode-both duration-700'>
                                <p className='type-eyebrow flex items-center gap-3 text-ember'>
                                    <span aria-hidden='true' className='h-px w-8 bg-current' />
                                    Coaching
                                </p>

                                {!offer.bestaetigt && <DraftBadge />}
                            </div>

                            <h1
                                id='seite-titel'
                                data-mdlfy-cms={cms('angebote', offer.id, 'name')}
                                className='type-display text-display-xl animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-100 duration-1000 ease-brand'
                            >
                                {offer.name}
                            </h1>

                            {hasValue(offer.kurzbeschreibung) && (
                                <p
                                    data-mdlfy-cms={cms('angebote', offer.id, 'kurzbeschreibung')}
                                    className='type-lead max-w-xl text-smoke animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-200 duration-1000 ease-brand'
                                >
                                    {offer.kurzbeschreibung}
                                </p>
                            )}

                            <div className='flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-3 fill-mode-both delay-300 duration-1000 ease-brand'>
                                <OfferSpecs offer={offer} className='max-w-xl' />
                                <CtaButton settings={settings} href={requestHref} size='lg' className='self-start' />
                            </div>
                        </div>

                        <div className='lg:col-span-5 animate-in fade-in fill-mode-both delay-200 duration-1000'>
                            <OfferImage offer={offer} priority className='aspect-[4/5] w-full' />
                        </div>
                    </div>
                </Container>
            </section>

            <section aria-label='Details zum Angebot' className='relative bg-paper py-24 sm:py-32 lg:py-40'>
                <Container>
                    {hasValue(offer.beschreibung) && (
                        <p
                            data-mdlfy-cms={cms('angebote', offer.id, 'beschreibung')}
                            className={cn('mb-20 max-w-4xl whitespace-pre-line text-2xl font-medium leading-snug tracking-[-0.015em] text-ink sm:text-[2rem] lg:mb-28', isPlaceholder(offer.beschreibung) && 'is-placeholder')}
                            {...reveal()}
                        >
                            {offer.beschreibung}
                        </p>
                    )}

                    {hasValue(offer.zielgruppe) && (
                        <DetailRow label={SPEC_LABELS.zielgruppe}>
                            <p
                                data-mdlfy-cms={cms('angebote', offer.id, 'zielgruppe')}
                                className={cn('max-w-2xl text-lg leading-relaxed text-ink/85', isPlaceholder(offer.zielgruppe) && 'is-placeholder')}
                            >
                                {offer.zielgruppe}
                            </p>
                        </DetailRow>
                    )}

                    {offer.leistungsumfang.length > 0 && (
                        <DetailRow label='Leistungsumfang'>
                            <ul className='flex max-w-2xl flex-col gap-4'>
                                {offer.leistungsumfang.map((entry, index) => (
                                    <li key={`${index}-${entry}`} className='flex items-start gap-4 text-lg leading-relaxed text-ink/85'>
                                        <span aria-hidden='true' className='mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-ink text-bone'>
                                            <Check className='size-3.5' strokeWidth={2.5} />
                                        </span>

                                        <span className={cn(isPlaceholder(entry) && 'is-placeholder')}>
                                            {entry}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </DetailRow>
                    )}

                    {hasValue(offer.ablauf) && (
                        <DetailRow label={SPEC_LABELS.ablauf}>
                            <p
                                data-mdlfy-cms={cms('angebote', offer.id, 'ablauf')}
                                className={cn('max-w-2xl text-lg leading-relaxed text-ink/85', isPlaceholder(offer.ablauf) && 'is-placeholder')}
                            >
                                {offer.ablauf}
                            </p>
                        </DetailRow>
                    )}

                    <DetailRow label='Rahmen'>
                        <OfferSpecs offer={offer} className='max-w-2xl' />
                    </DetailRow>

                    <div className='mt-12 flex flex-col items-start gap-4 border-t border-ink/15 pt-10 sm:flex-row sm:items-center sm:justify-between' {...reveal()}>
                        <p className='max-w-md text-[0.9375rem] leading-relaxed text-smoke'>
                            <span>Interesse an </span>
                            <span data-mdlfy-cms={cms('angebote', offer.id, 'name')}>{offer.name}</span>
                            <span>? Im Erstgespräch klären wir, ob es zu dir passt.</span>
                        </p>

                        <CtaButton settings={settings} href={requestHref} />
                    </div>
                </Container>
            </section>

            {others.length > 0 && (
                <section aria-labelledby='weitere-titel' className='relative bg-bone py-24 sm:py-32'>
                    <Container>
                        <SectionIntro block={blocks['coaching.detail']} headingId='weitere-titel' size='md' className='max-w-2xl' />

                        <ul className='mt-12 border-t border-ink/15'>
                            {others.map((other, index) => (
                                <li key={other.id} className='border-b border-ink/15' {...reveal(index * 80)}>
                                    <Link href={`/coaching/${other.slug}`} className='group flex items-center gap-6 py-7 sm:gap-10'>
                                        <span aria-hidden='true' className='type-label w-6 text-ember'>
                                            {padNumber(index)}
                                        </span>

                                        <span className='flex flex-1 flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8'>
                                            <span data-mdlfy-cms={cms('angebote', other.id, 'name')} className='type-display text-display-sm transition-colors duration-300 group-hover:text-ember'>
                                                {other.name}
                                            </span>

                                            {hasValue(other.kurzbeschreibung) && (
                                                <span data-mdlfy-cms={cms('angebote', other.id, 'kurzbeschreibung')} className='max-w-md text-[0.9375rem] text-smoke sm:text-right'>
                                                    {other.kurzbeschreibung}
                                                </span>
                                            )}
                                        </span>

                                        <span aria-hidden='true' className='flex size-10 shrink-0 items-center justify-center rounded-full border border-ink/20 transition-[transform,background-color,color] duration-500 ease-brand group-hover:rotate-45 group-hover:bg-ink group-hover:text-bone'>
                                            <ArrowUpRight className='size-4' />
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </Container>
                </section>
            )}

            <FaqSection id='angebot-faq' block={blocks['coaching.faq']} items={faqs} />
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    const slugs = await getOfferSlugs()

    return {
        paths: slugs.map(slug => ({ params: { slug } })),
        fallback: 'blocking'
    }
}

export const getStaticProps: GetStaticProps<OfferPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''
    const offer = slug ? await getOfferBySlug(slug) : null

    if (!offer) return { notFound: true, revalidate: REVALIDATE_SECONDS }

    const [content, offers, faqs] = await Promise.all([
        getPageContent({ blocks: ['coaching.detail', 'coaching.faq'] }),
        getOffers(),
        getFaqs({ category: 'Coaching' })
    ])

    return {
        props: {
            frame: content.frame,
            blocks: content.blocks,
            offer,
            others: offers.filter(other => other.id !== offer.id),
            faqs
        },
        revalidate: REVALIDATE_SECONDS
    }
}

export default OfferPage
